import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drive the app's automatic sign-out from the session lifetime Orcanos reports at login.
 *
 * The backend already computes it: `qw_login` returns `Configurations.Idle_time`, the
 * backend turns that (or SESSION_TIMEOUT) into a DB expiry, and `/api/auth/status`
 * reports what is left as `expiry_seconds`. Nothing consumed it — so an expired session
 * only surfaced as a 401 on whatever the user clicked next, which reads as the app being
 * broken rather than as a sign-out. This closes that loop.
 *
 * Two clocks, on purpose:
 *   - a 1 s LOCAL tick off an absolute deadline (`Date.now()`-based, so a sleeping laptop
 *     wakes up already expired instead of resuming a stale countdown), and
 *   - a 30 s RESYNC against `/api/auth/status`, which is authoritative and — critically —
 *     does NOT extend the session. Every OTHER endpoint slides the expiry forward, so
 *     polling any of them would defeat the timeout entirely. `/api/auth/keepalive` is the
 *     one that deliberately extends, and it is only ever called from "Stay signed in".
 *
 * Resync also runs when the tab becomes visible again: a background tab's timers are
 * throttled, and work done in another tab may have extended the session behind our back.
 */

const POLL_MS = 30000;
const WARN_SECONDS = 120;

export default function useSessionExpiry({ enabled, onExpire, onIdentity }) {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const deadlineRef = useRef(null);   // epoch ms, or null when unknown
  const firedRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  // Every successful /status resync also reports WHO the cookie now belongs to. The caller
  // uses it to catch a tab whose session was replaced by a sign-in on another account in the
  // same browser (Critical Note #42) — it rides on this poll rather than adding a second one,
  // and only on /status, the one path that never extends the session.
  const onIdentityRef = useRef(onIdentity);
  onIdentityRef.current = onIdentity;

  const expire = useCallback(() => {
    if (firedRef.current) return;     // one sign-out, however many clocks noticed
    firedRef.current = true;
    deadlineRef.current = null;
    setSecondsLeft(0);
    if (onExpireRef.current) onExpireRef.current();
  }, []);

  const sync = useCallback(async (path) => {
    try {
      const resp = await fetch(path || '/api/auth/status', { credentials: 'include' });
      if (resp.status === 401) { expire(); return; }
      // A transient 5xx must NOT sign anyone out — keep the countdown we already have.
      if (!resp.ok) return;
      const data = await resp.json();
      if (!path && onIdentityRef.current) onIdentityRef.current(data);
      const secs = Number(data.expiry_seconds);
      if (!Number.isFinite(secs)) return;
      if (secs <= 0) { expire(); return; }
      deadlineRef.current = Date.now() + secs * 1000;
      setSecondsLeft(Math.round(secs));
    } catch (e) {
      // Offline / network blip — same rule as a 5xx: keep counting, don't sign out.
    }
  }, [expire]);

  const stayAlive = useCallback(() => sync('/api/auth/keepalive'), [sync]);

  useEffect(() => {
    if (!enabled) {
      deadlineRef.current = null;
      firedRef.current = false;       // re-arm for the next login in this page load
      setSecondsLeft(null);
      return undefined;
    }

    sync();
    const poll = setInterval(() => sync(), POLL_MS);
    const tick = setInterval(() => {
      if (deadlineRef.current == null) return;
      const left = Math.round((deadlineRef.current - Date.now()) / 1000);
      setSecondsLeft(left > 0 ? left : 0);
      if (left <= 0) expire();
    }, 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') sync(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(poll);
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, sync, expire]);

  return {
    secondsLeft,
    warn: secondsLeft !== null && secondsLeft > 0 && secondsLeft <= WARN_SECONDS,
    stayAlive,
  };
}
